import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Layout } from "@/components/Layout";
import { ttpaPageQueryOptions } from "@/lib/ttpa-public.functions";

const dateFmt = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "long",
  year: "numeric",
});

function excerpt(md: string, max = 160): string {
  const text = md
    .replace(/<!--[\s\S]*?-->/g, " ")
    .replace(/```[\s\S]*?```/g, " ")

    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[[^\]]*\]\([^)]*\)/g, (m) => m.replace(/\[([^\]]*)\]\([^)]*\)/, "$1"))
    .replace(/[#>*_`~-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (text.length <= max) return text;
  return text.slice(0, max - 1).replace(/\s+\S*$/, "") + "…";
}

export const Route = createFileRoute("/ttpa/$slug")({
  loader: async ({ context, params }) => {
    const page = await context.queryClient.ensureQueryData(
      ttpaPageQueryOptions(params.slug),
    );
    if (!page) throw notFound();
    return page;
  },
  head: ({ loaderData }) => {
    const title = loaderData?.title
      ? `${loaderData.title} – creategy`
      : "Transparenzseite – creategy";
    const description = loaderData
      ? excerpt(loaderData.content_md) || "Transparenzhinweise gemäß TTPA."
      : "Transparenzhinweise gemäß TTPA.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "noindex, nofollow" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
      ],
    };
  },
  notFoundComponent: () => (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 md:px-12 section-y-hero text-center">
        <h1 className="heading-h1">Seite nicht gefunden.</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Diese Transparenzseite existiert nicht oder ist nicht veröffentlicht.
        </p>
        <Link to="/" className="mt-6 inline-block text-sm font-semibold underline">
          Zur Startseite
        </Link>
      </div>
    </Layout>
  ),
  errorComponent: () => (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 md:px-12 section-y-hero text-center">
        <h1 className="heading-h1">Seite konnte nicht geladen werden.</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Bitte versuchen Sie es später erneut.
        </p>
      </div>
    </Layout>
  ),
  component: TtpaPage,
});

function TtpaPage() {
  const { slug } = Route.useParams();
  const { data: page } = useSuspenseQuery(ttpaPageQueryOptions(slug));

  if (!page) throw notFound();

  return (
    <Layout>
      <section>
        <div className="mx-auto max-w-3xl px-6 md:px-12 section-y-hero">
          <h1 className="heading-h1">{page.title}</h1>
          <p className="mt-4 text-xs text-muted-foreground">
            Zuletzt aktualisiert am {dateFmt.format(new Date(page.updated_at))}
          </p>
          <div className="markdown-body markdown-article mt-10">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{page.content_md}</ReactMarkdown>
          </div>
        </div>
      </section>
    </Layout>
  );
}
